import {
  Assembly,
  AssemblyAlias,
  AssemblyAliasCreateRequest,
  AssemblyCreateRequest,
  AssemblyUpdateRequest,
  Component,
  ComponentCreateRequest,
  ComponentUpdateRequest,
} from '@/app/types/assemblies';
import { ApiClient, ApiClientProps, Paginated, PaginationParams } from './client';

/**
 * API client for assemblies-related endpoints
 */
export class AssembliesClient extends ApiClient {
  constructor(props: ApiClientProps) {
    super(props);
  }

  /**
   * Fetch all assemblies
   * @throws {Error} If the API request fails
   */
  async getAll(props?: PaginationParams) {
    return this._get<Paginated<Assembly>>('assemblies/', props);
  }

  /**
   * Fetch a single assembly by ID
   * @throws {Error} If the API request fails
   */
  async getById(id: string) {
    return this._get<Assembly>(`assemblies/${id}/`);
  }

  /**
   * Create a new assembly
   * @throws {Error} If the API request fails
   */
  async create(data: AssemblyCreateRequest) {
    return this._post<Assembly>('assemblies/', data);
  }

  /**
   * Update an assembly
   * @throws {Error} If the API request fails
   */
  async update(id: string, data: AssemblyUpdateRequest) {
    return this._patch<Assembly>(`assemblies/${id}/`, data);
  }

  /**
   * Delete an assembly by ID
   * @throws {Error} If the API request fails
   */
  async delete(id: string) {
    return this._delete(`assemblies/${id}/`);
  }

  /**
   * Duplicate an assembly with all its components and aliases
   * @throws {Error} If the API request fails
   */
  async duplicate(id: string) {
    return this._post<Assembly>(`assemblies/${id}/duplicate/`);
  }

  /**
   * Get all components for an assembly
   * @throws {Error} If the API request fails
   */
  async getComponents(assemblyId: string) {
    return this._get<Component[]>(`assemblies/${assemblyId}/components/`);
  }

  /**
   * Create a new component
   * @throws {Error} If the API request fails
   */
  async createComponent(assemblyId: string, data: ComponentCreateRequest) {
    return this._post<Component>(`assemblies/${assemblyId}/components/`, data);
  }

  /**
   * Update a component
   * @throws {Error} If the API request fails
   */
  async updateComponent(assemblyId: string, id: string, data: ComponentUpdateRequest) {
    return this._patch<Component>(`assemblies/${assemblyId}/components/${id}/`, data);
  }

  /**
   * Delete a component by ID
   * @throws {Error} If the API request fails
   */
  async deleteComponent(assemblyId: string, id: string) {
    return this._delete(`assemblies/${assemblyId}/components/${id}/`);
  }

  /**
   * Get all aliases for an assembly
   * @throws {Error} If the API request fails
   */
  async getAliases(assemblyId: string) {
    return this._get<AssemblyAlias[]>(`assemblies/${assemblyId}/aliases/`);
  }

  /**
   * Create a new alias
   * @throws {Error} If the API request fails
   */
  async createAlias(data: AssemblyAliasCreateRequest) {
    return this._post<AssemblyAlias>(`assemblies/${data.assembly_id}/aliases/`, data);
  }

  /**
   * Delete an alias by ID
   * @throws {Error} If the API request fails
   */
  async deleteAlias(assemblyId: string, id: string) {
    return this._delete(`assemblies/${assemblyId}/aliases/${id}/`);
  }
}
